import { Box, TableCell, TableRow } from "@mui/material";
import { useMasterDataBreadCrumbProvider } from "../../useMasterDataBreadCrumbProvider";
import { useTranslation } from "../../../localization/useTranslation";
import { DotLegalOverflowMenu, DotLegalPageHeader, DotLegalTableWithControls, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import CheckIcon from "@mui/icons-material/Check";
import { RiskVersionViewModel } from "./RiskAssessmentVersions.types";
import useRiskAssessmentVersionsDataMapping from "./RiskAssessmentVersions.hooks";
import { ChangeRiskAssessmentVersionLockedDialog } from "../changeRiskAssessmentVersionLockedDialog/ChangeRiskAssessmentVersionLockedDialog";
import { DeleteRiskAssessmentVersionDialog } from "../deleteRiskAssessmentVersionDialog/DeleteRiskAssessmentVersionDialog";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import EditRiskAssessmentVersionDialog from "../editRiskAssessmentVersionDialog/EditRiskAssessmentVersionDialog";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function RiskAssessmentVersions() {
    const { translateString, translateDate, translateRiskAssessmentVersionAreas } = useTranslation();
    const { getBreadCrumbs } = useMasterDataBreadCrumbProvider();
    const { permissions } = useUserContext();

    const {
        data,
        isLoading,
        selectedVersion,
        setSelectedVersion,
        setShowChangeLockedStatusDialog,
        showChangeLockedStatusDialog,
        onSave,
        setShowDeleteDialog,
        showDeleteDialog,
        onDelete,
        setVersionForEdit,
        versionForEdit,
    } = useRiskAssessmentVersionsDataMapping();

    const getMenuItems = (row: RiskVersionViewModel) => {
        let menuItems = [];

        if (permissions.riskVersionPermissions.edit) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setVersionForEdit(row);
                },
            });
        }

        if (permissions.riskVersionPermissions.delete) {
            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setSelectedVersion(row);
                    setShowDeleteDialog(true);
                },
            });
        }

        if (permissions.riskVersionPermissions.edit) {
            menuItems.push({
                menuItem: row.isLocked ? translateString("unlock") : translateString("lock"),
                onClick: () => {
                    setSelectedVersion(row);
                    setShowChangeLockedStatusDialog(true);
                },
            });
        }

        return menuItems;
    };

    function getHeaders() {
        let headers = Array<ITableHeader<RiskVersionViewModel>>();
        headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
        headers.push({ property: "description", text: translateString("description"), align: "left", showOnMobile: true });
        headers.push({ property: "created", text: translateString("created"), align: "left", showOnMobile: true });
        headers.push({ property: "area", text: translateString("riskArea"), align: "left", showOnMobile: true });
        headers.push({ property: "isLocked", text: translateString("locked"), align: "left", showOnMobile: true });
        headers.push({ property: "isCurrent", text: translateString("currentRisk"), align: "left", showOnMobile: true });

        return headers;
    }

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader breadCrumbs={getBreadCrumbs(translateString("riskVersions"))} />

            <TablePagePaper>
                <DotLegalTableWithControls
                    internalScroll
                    headers={getHeaders()}
                    getUserSpecificPageLength={() => 30}
                    hideRowsPerPage
                    clickableRows={false}
                    hasOverflowMenu
                    defaultOrderBy={"created"}
                    defaultOrder={"desc"}
                    isLoading={isLoading}
                    emptyText={translateString("noData")}
                    labelRowsPerPage={translateString("showEntites")}
                    labelSearch={translateString("search")}
                    noOptionsLabel={translateString("noOptions")}
                    paginationLabelOf={translateString("labelOf")}
                    data={data}
                    renderRow={(row) => (
                        <TableRow key={row.id}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{row.description}</TableCell>
                            <TableCell>{translateDate(row.created)}</TableCell>
                            <TableCell>{translateRiskAssessmentVersionAreas(row.area.toString())}</TableCell>
                            <TableCell>
                                <Box>{row.isLocked ? <CheckIcon /> : ""}</Box>
                            </TableCell>
                            <TableCell>
                                <Box>{row.isCurrent ? <CheckIcon /> : ""}</Box>
                            </TableCell>
                            <TableCell align={"right"}>
                                {permissions.riskVersionPermissions.edit && <DotLegalOverflowMenu items={getMenuItems(row)} />}
                            </TableCell>
                        </TableRow>
                    )}
                />
            </TablePagePaper>

            {versionForEdit && (
                <EditRiskAssessmentVersionDialog
                    onDialogClose={() => setVersionForEdit(undefined)}
                    onVersionSaved={() => {
                        setVersionForEdit(undefined);
                        onSave();
                    }}
                    selectedVersion={versionForEdit}
                />
            )}

            {showChangeLockedStatusDialog && (
                <ChangeRiskAssessmentVersionLockedDialog
                    selectedVersion={selectedVersion!}
                    onCloseDialog={() => {
                        setShowChangeLockedStatusDialog(false);
                        setSelectedVersion(undefined);
                    }}
                    onSave={() => {
                        setShowChangeLockedStatusDialog(false);
                        setSelectedVersion(undefined);
                        onSave();
                    }}
                />
            )}

            {showDeleteDialog && (
                <DeleteRiskAssessmentVersionDialog onCloseDialog={() => setShowDeleteDialog(false)} onDeletion={onDelete} selectedVersion={selectedVersion!} />
            )}
        </Box>
    );
}

export default RiskAssessmentVersions;
